/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — ValidationRuleBuilder orchestrator
 *
 * Composes the three sub-primitives — LogicalOperatorToggle, ConditionRow,
 * and FailureActionPicker — into a complete validation rule builder.
 */
import cx from 'classnames';
import React from 'react';

import Button from '../../components/Button/Button';
import type {
  FailureActionOption,
  FieldSelectRenderProps,
  ValidationCondition,
  ValidationField,
  ValidationLogicalOperator,
  ValidationOperator,
  ValueInputRenderProps,
} from '../../types';
import ConditionRow from './ConditionRow';
import FailureActionPicker from './FailureActionPicker';
import LogicalOperatorToggle from './LogicalOperatorToggle';

import './ValidationRuleBuilder.less';

export interface ValidationRuleBuilderProps {
  conditions: ValidationCondition[];
  onConditionsChange: (next: ValidationCondition[]) => void;

  logicalOperator: ValidationLogicalOperator;
  onLogicalOperatorChange: (next: ValidationLogicalOperator) => void;

  failureAction: string;
  failureActionOptions: FailureActionOption[];
  onFailureActionChange: (next: string) => void;

  operators: ValidationOperator[];

  disabled?: boolean;

  /**
   * Available fields for the field select column in each condition row.
   * When omitted the field column is not rendered.
   */
  fields?: ValidationField[];
  /**
   * Renders the field column as a permanently-disabled Select showing this
   * field as context. Use when the field is fixed by the parent (e.g. a
   * per-field validation builder). Forwarded to every ConditionRow.
   */
  fixedField?: ValidationField;
  /**
   * Escape hatch — overrides the default DS Select used to pick the field in
   * each condition row. Not called when `fields` is empty or undefined.
   */
  renderFieldSelect?: (props: FieldSelectRenderProps) => React.ReactNode;

  renderValueInput?: (props: ValueInputRenderProps) => React.ReactNode;

  /** Label above the failure-action group. Default: 'What should happen if validation fails?' */
  failureActionLabel?: React.ReactNode;
  /** Text for the add-condition button. Default: 'Add condition' */
  addConditionLabel?: React.ReactNode;

  className?: string;
}

const DEFAULT_FAILURE_ACTION_LABEL = 'What should happen if validation fails?';
const DEFAULT_ADD_CONDITION_LABEL = 'Add condition';

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function') {
    return (crypto as Crypto).randomUUID();
  }
  // Jest/JSDOM fallback
  return `ds-vrb-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

const ValidationRuleBuilder: React.FC<ValidationRuleBuilderProps> = ({
  conditions,
  onConditionsChange,
  logicalOperator,
  onLogicalOperatorChange,
  failureAction,
  failureActionOptions,
  onFailureActionChange,
  operators,
  disabled,
  fields,
  fixedField,
  renderFieldSelect,
  renderValueInput,
  failureActionLabel,
  addConditionLabel,
  className,
}) => {
  const handleConditionChange = React.useCallback(
    (id: string, updates: Partial<ValidationCondition>) => {
      const next = conditions.map((c) => (c.id === id ? { ...c, ...updates } : c));
      onConditionsChange(next);
    },
    [conditions, onConditionsChange]
  );

  const handleConditionRemove = React.useCallback(
    (id: string) => {
      const next = conditions.filter((c) => c.id !== id);
      onConditionsChange(next);
    },
    [conditions, onConditionsChange]
  );

  const handleAddCondition = React.useCallback(() => {
    const newCondition: ValidationCondition = { id: generateId(), operator: '', value: undefined };
    onConditionsChange([...conditions, newCondition]);
  }, [conditions, onConditionsChange]);

  const showLogicalToggle = conditions.length > 1;

  return (
    <div className={cx('ds-validation-rule-builder', className)}>
      {showLogicalToggle ? (
        <div className="ds-validation-rule-builder__condition-block">
          <div className="ds-validation-rule-builder__toggle-rail">
            <LogicalOperatorToggle value={logicalOperator} onChange={onLogicalOperatorChange} disabled={disabled} />
          </div>
          <div className="ds-validation-rule-builder__condition-list">
            {conditions.map((condition) => (
              <ConditionRow
                key={condition.id}
                condition={condition}
                operators={operators}
                onChange={(updates) => handleConditionChange(condition.id, updates)}
                onRemove={() => handleConditionRemove(condition.id)}
                disabled={disabled}
                fields={fields}
                fixedField={fixedField}
                renderFieldSelect={renderFieldSelect}
                renderValueInput={renderValueInput}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="ds-validation-rule-builder__condition-list">
          {conditions.map((condition) => (
            <ConditionRow
              key={condition.id}
              condition={condition}
              operators={operators}
              onChange={(updates) => handleConditionChange(condition.id, updates)}
              onRemove={() => handleConditionRemove(condition.id)}
              disabled={disabled}
              fields={fields}
              fixedField={fixedField}
              renderFieldSelect={renderFieldSelect}
              renderValueInput={renderValueInput}
            />
          ))}
        </div>
      )}

      <Button
        variant="tertiary"
        size="medium"
        disabled={disabled}
        onClick={handleAddCondition}
        className="ds-validation-rule-builder__add-button"
        iconLeft="actionable/add">
        {addConditionLabel ?? DEFAULT_ADD_CONDITION_LABEL}
      </Button>

      <div className="ds-validation-rule-builder__failure-action">
        <FailureActionPicker
          value={failureAction}
          options={failureActionOptions}
          onChange={onFailureActionChange}
          disabled={disabled}
          label={failureActionLabel ?? DEFAULT_FAILURE_ACTION_LABEL}
        />
      </div>
    </div>
  );
};

ValidationRuleBuilder.displayName = 'ValidationRuleBuilder';

export { ValidationRuleBuilder };
export default ValidationRuleBuilder;
